import type { ReactNode } from 'react'
import { ArrowRight } from 'lucide-react'
import type { ResultItem } from './types'

function SquareTag({
    tone,
    children,
}: {
    tone: 'warning' | 'success' | 'info' | 'neutral'
    children: ReactNode
}) {
    const colorClass = {
        warning: 'bg-warning',
        success: 'bg-success',
        info: 'bg-primary',
        neutral: 'bg-muted-foreground',
    }[tone]

    return (
        <span className='border-border bg-card text-muted-foreground inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-xs font-bold'>
            <span
                className={`size-2 rounded-[1px] ${colorClass}`}
                aria-hidden='true'
            />
            {children}
        </span>
    )
}

function ChangeFlow({ item }: { item: ResultItem }) {
    if (!item.current_state && !item.expected_change) return null

    return (
        <div className='mt-3 grid grid-cols-[minmax(0,1fr)_28px_minmax(0,1fr)] items-stretch gap-2 max-md:grid-cols-1'>
            <div className='border-border bg-muted/45 rounded-sm border p-3'>
                <div className='text-muted-foreground text-xs font-bold'>
                    현재
                </div>
                <div className='text-foreground mt-1 text-sm'>
                    {item.current_state ?? '-'}
                </div>
            </div>
            <div className='text-muted-foreground grid place-items-center max-md:rotate-90'>
                <ArrowRight className='size-4' />
            </div>
            <div className='border-border bg-accent rounded-sm border p-3'>
                <div className='text-primary text-xs font-bold'>변경 후</div>
                <div className='text-foreground mt-1 text-sm font-medium'>
                    {item.expected_change ?? '-'}
                </div>
            </div>
        </div>
    )
}

export function ResultItemButton({
    item,
    onSelect,
}: {
    item: ResultItem
    onSelect(itemId: string): void
}) {
    return (
        <button
            type='button'
            onClick={() => onSelect(item.item_id)}
            className='border-border hover:border-primary rounded-sm border p-4 text-left transition-colors'>
            <div className='flex flex-wrap items-start justify-between gap-2'>
                <div>
                    <h3 className='text-foreground text-base font-bold'>
                        {item.title ?? item.item_id}
                    </h3>
                    <p className='text-muted-foreground mt-1 text-xs font-medium'>
                        {item.item_id} · {item.checklistPath} ·{' '}
                        {item.checklistBasis}
                    </p>
                </div>
                <SquareTag tone={item.recheck_required ? 'warning' : 'success'}>
                    {item.statusLabel}
                </SquareTag>
            </div>
            <ChangeFlow item={item} />
            {item.recommendation ? (
                <p className='border-border bg-muted/35 text-muted-foreground mt-3 rounded-sm border px-3 py-2 text-sm'>
                    <span className='text-foreground mr-2 font-bold'>권고</span>
                    {item.recommendation}
                </p>
            ) : null}
        </button>
    )
}
